import { useState } from "react";
import { deepDives } from "@/mocks/deepDives";
import SectionHeading from "./SectionHeading";

function formatHeat(value: number) {
  return `${(value / 10000).toFixed(1)} 万`;
}

export default function DeepDive() {
  const [activeId, setActiveId] = useState(deepDives[0].id);
  const active = deepDives.find((item) => item.id === activeId) ?? deepDives[0];

  return (
    <section id="detail" className="scroll-mt-24 w-full bg-background-100">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="深度专题 · 事件脉络"
          title="一件事的前因后果，一次看完"
          desc="热门事件常常散落在几十条快讯里。深度专题把它重新拼成一条脉络，标出已经落地的节点和还没确定的部分，省得你来回翻。"
          tone="primary"
        />

        <div className="mt-8 flex flex-wrap gap-2">
          {deepDives.map((item) => {
            const isActive = item.id === activeId;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveId(item.id)}
                aria-pressed={isActive}
                className={`px-4 py-2 rounded-md text-sm text-left transition-colors cursor-pointer border max-w-full ${
                  isActive
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-300"
                }`}
              >
                <span className="block text-xs opacity-80">{item.tag}</span>
                <span className="block truncate">{item.category}专题</span>
              </button>
            );
          })}
        </div>

        <article key={active.id} className="mt-6 rounded-lg border border-background-200 bg-background-50 overflow-hidden animate-fade-up">
          <div className="relative w-full h-[220px] md:h-[320px]">
            <img
              src={active.cover}
              alt={`${active.title}专题封面`}
              title={`${active.title} - 瓜田速递深度专题`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent"></div>
            <div className="absolute left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs">{active.tag}</span>
                <span className="px-2.5 py-1 rounded-full bg-black/45 text-background-100 text-xs backdrop-blur-sm">
                  {active.updated}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/45 text-background-100 text-xs backdrop-blur-sm">
                  <i className="ri-fire-line" aria-hidden="true"></i>
                  {formatHeat(active.heat)} 热度
                </span>
              </div>
              <h3 className="mt-3 font-heading text-2xl md:text-3xl text-background-50 leading-snug max-w-3xl">
                {active.title}
              </h3>
            </div>
          </div>

          <div className="p-4 md:p-6">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">{active.intro}</p>

            <h4 className="mt-7 font-heading text-xl text-foreground-950">事件脉络 · 关键节点</h4>
            <ol className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {active.stages.map((stage, index) => (
                <li
                  key={stage.title}
                  className="rounded-md border border-background-200 bg-background-100 p-4 relative"
                >
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-primary-100 text-primary-700 text-xs">
                    <i className="ri-map-pin-2-line" aria-hidden="true"></i>
                    {stage.time}
                  </span>
                  <h5 className="mt-2 text-base font-medium text-foreground-950 leading-snug">
                    {index + 1}. {stage.title}
                  </h5>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{stage.desc}</p>
                </li>
              ))}
            </ol>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#live"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-foreground-900 text-background-50 text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-history-line" aria-hidden="true"></i>
                查看该事件全部快讯
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-notification-3-line" aria-hidden="true"></i>
                订阅后续进展
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}